import { z } from "zod";
import { PatientDocumentFileSchema, } from "./generated-patient-documents.js";
import { resolveLetterContent } from "./letters.js";
import { PatientMessageChannelSchema, PatientMessageReasonSchema, PatientMessageRecipientMaskSchema, PatientMessageStatusSchema, PatientMessageTransportSchema, patientMessageReasonFits, patientMessageRecipientMaskFits, } from "./patient-messaging.js";
import { PatientMessagingConsentSchema, PatientMessagingOfferedChannelsSchema, } from "./patient-messaging-consent.js";
import { IanaTimeZoneSchema, IdempotencyKeySchema, IdSchema, IsoDateTimeSchema, } from "./primitives.js";
export const PATIENT_DOCUMENT_DELIVERY_TYPES = [
    "sick_note",
    "school_note",
    "return_to_work",
    "general",
    "handout",
];
export const PatientDocumentDeliveryTypeSchema = z.enum(PATIENT_DOCUMENT_DELIVERY_TYPES);
export function patientDocumentDeliveryType(content) {
    return content.kind === "handout" ? "handout" : resolveLetterContent(content.letter).type;
}
export const PUBLIC_DOCUMENT_LINK_HOURS = 72;
export const PUBLIC_DOCUMENT_LINK_DATE_OF_BIRTH_ATTEMPT_LIMIT = 3;
export const OpenPublicDocumentRequestSchema = z
    .object({ token: z.string().regex(/^[A-Za-z0-9_-]{43}$/), dateOfBirth: z.iso.date() })
    .strict();
export const PublicDocumentSessionSchema = z
    .object({
    clinicName: z.string().min(1).max(240),
    clinicPhone: z.string().max(80).nullable(),
    timeZone: IanaTimeZoneSchema,
    title: z.string().min(1).max(240),
    issuedAt: IsoDateTimeSchema,
    fileName: z.string().min(1).max(240),
    pageCount: z.number().int().positive(),
    byteSize: z.number().int().positive(),
    expiresAt: IsoDateTimeSchema,
})
    .strict();
/** Server-only storage reference. The public response never carries a bucket, object name or hash. */
export const PublicDocumentFileRecordSchema = z
    .object({ deliveryId: IdSchema, file: PatientDocumentFileSchema })
    .strict();
export const PublicDocumentRefusalSchema = z.object({ refused: z.literal(true) }).strict();
/** Coarse first-open information only; raw user agents and address hashes remain in the audit store. */
export const PatientDocumentOpenOriginSchema = z
    .object({
    device: z.enum(["phone", "tablet", "computer", "unknown"]),
    browser: z.enum(["Safari", "Chrome", "Firefox", "Edge", "Other"]),
})
    .strict();
export const PatientDocumentDeliverySchema = z
    .object({
    id: IdSchema,
    documentId: IdSchema,
    documentVersion: z.number().int().positive(),
    channel: PatientMessageChannelSchema,
    recipientMasked: PatientMessageRecipientMaskSchema,
    sentByName: z.string().min(1).max(200),
    createdAt: IsoDateTimeSchema,
    expiresAt: IsoDateTimeSchema,
    message: z
        .object({
        status: PatientMessageStatusSchema,
        reason: PatientMessageReasonSchema.nullable(),
        transport: PatientMessageTransportSchema.nullable(),
    })
        .strict(),
    openedAt: IsoDateTimeSchema.nullable(),
    openedFrom: PatientDocumentOpenOriginSchema.nullable(),
    downloadedAt: IsoDateTimeSchema.nullable(),
    revokedAt: IsoDateTimeSchema.nullable(),
    revokedByName: z.string().min(1).max(200).nullable(),
    locked: z.boolean(),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.openedAt === null) !== (value.openedFrom === null))
        context.addIssue({
            code: "custom",
            message: "First-open time and origin are paired",
            path: ["openedFrom"],
        });
    if ((value.revokedAt === null) !== (value.revokedByName === null))
        context.addIssue({
            code: "custom",
            message: "Revocation time and staff member are paired",
            path: ["revokedByName"],
        });
    if (!patientMessageRecipientMaskFits(value.channel, value.recipientMasked))
        context.addIssue({
            code: "custom",
            message: "Recipient mask matches its channel",
            path: ["recipientMasked"],
        });
    if (!patientMessageReasonFits(value.message.status, value.channel, value.message.reason))
        context.addIssue({
            code: "custom",
            message: "Message reason matches status and channel",
            path: ["message", "reason"],
        });
});
const ContactSchema = z
    .object({
    state: z.enum(["ok", "missing", "invalid"]),
    masked: PatientMessageRecipientMaskSchema.nullable(),
    stopped: z.boolean(),
})
    .strict();
export const PatientDocumentDeliveriesRecordSchema = z
    .object({
    timeZone: IanaTimeZoneSchema,
    offeredChannels: PatientMessagingOfferedChannelsSchema,
    consent: PatientMessagingConsentSchema,
    contacts: z.object({ sms: ContactSchema, email: ContactSchema }).strict(),
    enabledTypes: z.array(PatientDocumentDeliveryTypeSchema).max(5),
    documents: z.array(z
        .object({
        documentId: IdSchema,
        deliveryType: PatientDocumentDeliveryTypeSchema.nullable(),
        sendable: z.enum(["ok", "no_file", "superseded", "type_off"]),
        deliveries: z.array(PatientDocumentDeliverySchema).max(200),
    })
        .strict()),
})
    .strict();
export const PatientDocumentDeliveriesSchema = PatientDocumentDeliveriesRecordSchema.extend({
    patientLinksEnabled: z.boolean(),
});
export const SendPatientDocumentRequestSchema = z
    .object({
    documentId: IdSchema,
    channel: PatientMessageChannelSchema,
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const SendPatientDocumentResponseSchema = z
    .object({ created: z.boolean(), delivery: PatientDocumentDeliverySchema })
    .strict();
export const RevokePatientDocumentDeliveryResponseSchema = z
    .object({ delivery: PatientDocumentDeliverySchema })
    .strict();
//# sourceMappingURL=patient-document-delivery.js.map